import { useMemo, useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { OrderedStatuses } from 'business/quality-control/pages/segment-history/components/add-manuel-scan-modal/types';
import {
  LastScanRange,
  SegmentRange,
  SegmentDate,
} from 'business/quality-control/pages/segment-history/providers/filters';
import {
  GetFiltersDataQuery,
  Quality_SegmentHistoryStatusEnum_Enum,
} from 'generated/graphql';

export const useFilterOptions = (data: GetFiltersDataQuery | undefined) => {
  const { t } = useTranslation();

  const siteOptions = useMemo(() => {
    const options =
      data?.scanSites?.map((site) => ({
        label: site.label,
        value: site.id,
      })) || [];

    return options;
  }, [data]);

  const lastScanRangeOptions = useMemo(() => {
    return Object.values(LastScanRange).map((range) => ({
      label: t(`input.lastScanRange.selectOptions.option`, {
        context: range,
      }),
      value: range,
    }));
  }, [t]);

  const segmentRangeOptions = useMemo(() => {
    return Object.values(SegmentRange).map((range) => ({
      label: t(`input.segmentRange.selectOptions.option`, {
        context: range,
      }),
      value: range,
    }));
  }, [t]);

  const segmentTypeOptions = useMemo(() => {
    const options =
      data?.segmentTypes?.map((type) => ({
        label: type.segmentType,
        value: type.segmentType,
      })) || [];

    return options;
  }, [data]);

  const statusOptions = useMemo(() => {
    const anyQuarantineEnabled =
      data?.scanSites?.some(
        (site) => site.isQuarantineEnabled && site.isReceiptActivated,
      ) ?? false;
    const anyOnHoldEnabled =
      data?.scanSites?.some(
        (site) => site.isOnHoldOptionEnabled && site.isReceiptActivated,
      ) ?? false;

    return OrderedStatuses.filter(
      (s) =>
        anyQuarantineEnabled ||
        s !== Quality_SegmentHistoryStatusEnum_Enum.Quarantine,
    )
      .filter(
        (s) =>
          anyOnHoldEnabled ||
          s !== Quality_SegmentHistoryStatusEnum_Enum.OnHold,
      )
      .map((status) => ({
        value: status,
        label: t(`qualityControl.segmentHistory.status.state`, {
          context: status,
        }),
      }));
  }, [t, data?.scanSites]);

  const [firstSerialNumberOption, setFirstSerialNumberOption] = useState<
    string | null
  >(null);
  const serialNumberOptions = useMemo(() => {
    const options =
      data?.serialNumbers?.map((serialNumber) => ({
        value: serialNumber.serialNumber,
        label: serialNumber.serialNumber,
      })) || [];

    if (firstSerialNumberOption) {
      options.unshift({
        label: firstSerialNumberOption,
        value: firstSerialNumberOption,
      });
    }

    return options;
  }, [data, firstSerialNumberOption]);

  const segmentDateOptions = useMemo(() => {
    return Object.values(SegmentDate).map((date) => ({
      label: t(`input.segmentDate.selectOptions.option`, { context: date }),
      value: date,
    }));
  }, [t]);

  return {
    siteOptions,
    lastScanRangeOptions,
    segmentRangeOptions,
    segmentTypeOptions,
    statusOptions,
    serialNumberOptions,
    segmentDateOptions,
    setFirstSerialNumberOption,
  };
};
